import React from 'react';
import {
  ADMIN_MESSAGE_FORMATS,
  isHoldAnnouncementFormat,
  isInvoiceResubmissionRequestedFormat,
  isPartAnnouncementFormat,
  isStageSeatingShareAnnouncementFormat,
} from '../../../../constants/messageFormats';
import {
  buildSystemRichDescriptor,
  buildSystemTextDescriptor,
  getReminderHeader,
  renderSystemDescriptorCard,
  renderSystemTextCard,
} from './systemMessageUtils';

const DEFAULT_HOLD_DECLINE_TITLE = 'オファーのステータスが更新されました';
const DEFAULT_HOLD_DECLINE_BODY =
  '上記のオファーについて今回は参加を見送らせていただきます。また機会がありましたらよろしくお願いいたします。';

const isOfferOrContractAnnouncement = (format) =>
  [
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD,
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED,
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT,
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED,
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED,
  ].includes(format);

const isScheduleUpdateNoticeContent = (content = '') => {
  const text = String(content || '').trim();
  if (!text) return false;
  return (
    text.includes('【更新後スケジュール】') ||
    text.startsWith('スケジュールが更新されました') ||
    text.startsWith('スケジュールが確定しました') ||
    text.startsWith('スケジュールを含む契約内容が更新されました')
  );
};

const renderActionLink = (href, label, className = 'system-message-card__link') => {
  if (!href) return null;
  return (
    <div className="system-message-card__actions">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {label}
      </a>
    </div>
  );
};

const resolveStageSeatingShareTitle = (content = '') => (
  String(content || '').includes('共有')
    ? 'ステージシーティングが共有されました'
    : 'ステージシーティングが公開されました'
);

const renderStageSeatingShareContent = ({
  message,
  formatMessageContent,
  extractFirstUrl,
}) => {
  const bodyText = String(message?.content || '').trim() || 'ステージシーティングを確認できます。';
  const previewHref = extractFirstUrl?.(bodyText) || '';
  return (
    <>
      <div className="system-message-card__text">
        {formatMessageContent(bodyText)}
      </div>
      {renderActionLink(previewHref, 'プレビューを開く')}
    </>
  );
};

const resolveAnnouncementHeader = (format, titles = {}, message = {}) => {
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED) {
    return titles.contractCancelled || '契約が取り消されました。ご質問等はチャットにて行ってください。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED) {
    const content = String(message?.content || '');
    if (content.includes('再契約は不要')) {
      if (!isScheduleUpdateNoticeContent(content)) {
        return '契約内容が更新されました。詳細を確認してください。';
      }
      return 'スケジュールが更新されました。内容を確認してください。';
    }
    return titles.contractUpdated || '契約内容が更新されました。更新内容をチェックし再度契約を結ぶ必要があります。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED) {
    return titles.holdCancelled || 'オファーがキャンセルされました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD) {
    return titles.hold || 'オファーが届きました';
  }
  return titles.contract || '本契約が届きました';
};

const isScheduleNotifyOnlyUpdate = (message = {}) =>
  message?.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED &&
  isScheduleUpdateNoticeContent(message?.content);

const parseScheduleNoticeContent = (rawContent = '') => {
  const lines = String(rawContent || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const intro = [];
  const events = [];
  let currentEvent = null;
  let inScheduleBlock = false;

  lines.forEach((line) => {
    if (line === '【更新後スケジュール】') {
      inScheduleBlock = true;
      return;
    }
    const eventHeader = line.match(/^・(.+?)(?::\s*(.*))?$/);
    if (eventHeader && (inScheduleBlock || eventHeader[2])) {
      currentEvent = {
        title: eventHeader[1].trim(),
        schedules: [],
      };
      events.push(currentEvent);
      inScheduleBlock = true;
      if (eventHeader[2]) {
        const legacyRows = eventHeader[2].split(/\s+\/\s+/).filter(Boolean);
        legacyRows.forEach((row) => {
          const legacyMatch = row.match(/^(\d{4}-\d{2}-\d{2}|日付未設定)\s+([0-9:]{2,5})-([0-9:]{2,5})\s*(.*)$/);
          currentEvent.schedules.push({
            date: legacyMatch?.[1] || '',
            time: legacyMatch ? `${legacyMatch[2]}〜${legacyMatch[3]}` : '',
            note: legacyMatch?.[4] || row,
            place: '',
          });
        });
      }
      return;
    }
    const scheduleLine = line.match(/^[-－]\s*(.+)$/);
    if (scheduleLine && currentEvent) {
      const parts = scheduleLine[1].split('｜').map((part) => part.trim());
      const dateTimeMatch = parts[0]?.match(/^(\d{4}-\d{2}-\d{2}|日付未設定)\s+(.+)$/);
      currentEvent.schedules.push({
        date: dateTimeMatch?.[1] || '',
        time: dateTimeMatch?.[2] || parts[0] || '',
        note: parts[1] || '内容未設定',
        place: parts[2] || '',
      });
      return;
    }
    if (!inScheduleBlock || line.startsWith('変更点：') || line.includes('再契約は不要')) {
      intro.push(line);
    }
  });

  return { intro, events };
};

const resolveOtherEventsCount = (message, isHold) => {
  const raw = Number(isHold ? message?.hold_event_count : message?.contract_event_count);
  if (Number.isFinite(raw) && raw > 1) return raw - 1;
  if (isHold && message?.is_multiple_events) return 1;
  if (!isHold && message?.contract_is_multi) return 1;
  return 0;
};

const resolveEventAnnouncementFlyerUrl = ({
  message,
  cachedFlyerUrl,
  buildFlyerUrl,
  pickFlyerFilename,
}) => {
  const primaryFlyer = pickFlyerFilename?.(message) || '';
  return cachedFlyerUrl ?? (primaryFlyer && buildFlyerUrl ? buildFlyerUrl(primaryFlyer) : '');
};

const renderEventAnnouncementContent = ({
  message,
  formatMessageContent,
  flyerUrl,
  resolveGenreBadge,
  formatEventDate,
  formatEventTime,
  links,
}) => {
  const isHold = isHoldAnnouncementFormat(message?.message_format);
  const isHoldCancelled = message?.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED;
  const otherEventsCount = resolveOtherEventsCount(message, isHold);
  const eventName = isHold ? message?.event_name : message?.contract_event_name;
  const genre = isHold ? message?.event_genre : message?.contract_event_genre;
  const eventUuid = isHold
    ? message?.base_event_uuid || message?.event_uuid
    : message?.contract_base_event_uuid ||
      message?.contract_event_uuid ||
      message?.base_event_uuid ||
      message?.event_uuid;
  const eventPerformanceType = isHold
    ? message?.event_performance_type
    : message?.contract_event_performance_type;
  const eventDate = isHold ? message?.event_date : message?.contract_event_date;
  const startTime = isHold ? message?.event_start_time : message?.contract_event_start_time;
  const venue = isHold ? message?.event_venue : message?.contract_event_venue;
  const detailHref = isHold
    ? links?.holdDetailHref?.(message) || ''
    : links?.contractDetailHref?.(message) || '';
  const flyerHref = links?.eventDetailHref?.(eventUuid, message) || '';
  const genreBadge = resolveGenreBadge?.(genre) || null;
  const bodyText =
    String(message?.content || '').trim() ||
    (isHoldCancelled ? 'このオファーはキャンセルされました。' : '');
  const dateLabel = eventDate ? formatEventDate?.(eventDate) || '' : '';
  const timeLabel = startTime ? formatEventTime?.(startTime) || '' : '';
  const dateTimeLabel = [dateLabel, timeLabel].filter(Boolean).join(' ');
  const hasEventMeta = Boolean(dateTimeLabel || venue);
  const flyerNode = flyerUrl ? (
    flyerHref ? (
      <a
        href={flyerHref}
        target="_blank"
        rel="noopener noreferrer"
        className="system-message-card__event-flyer"
      >
        <img src={flyerUrl} alt="イベントフライヤー" />
      </a>
    ) : (
      <div className="system-message-card__event-flyer">
        <img src={flyerUrl} alt="イベントフライヤー" />
      </div>
    )
  ) : null;

  return (
    <>
      {bodyText && (
        <div className="system-message-card__text">
          {formatMessageContent(bodyText)}
        </div>
      )}
      {(flyerNode || eventName || eventPerformanceType || genreBadge || hasEventMeta) && (
        <div className="system-message-card__event">
          {flyerNode}
          <div className="system-message-card__event-info">
            {genreBadge && (
              <span
                className="system-message-card__event-genre"
                style={{
                  backgroundColor: genreBadge.backgroundColor,
                  color: genreBadge.color,
                }}
              >
                {genreBadge.label}
              </span>
            )}
            {eventName && <div className="system-message-card__event-name">{eventName}</div>}
            {eventPerformanceType && (
              <div className="system-message-card__event-type">{eventPerformanceType}</div>
            )}
            {hasEventMeta && (
              <div className="system-message-card__meta">
                {dateTimeLabel && (
                  <div className="system-message-card__meta-row">
                    <i className="fa-regular fa-calendar" aria-hidden="true" />
                    <span>{dateTimeLabel}</span>
                  </div>
                )}
                {venue && (
                  <div className="system-message-card__meta-row">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    <span>{venue}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {otherEventsCount > 0 && (
        <div className="system-message-card__event-extra">他{otherEventsCount}件のイベント</div>
      )}
      {renderActionLink(detailHref, '詳細を見る')}
    </>
  );
};

const renderScheduleUpdateAnnouncementContent = ({
  message,
  formatMessageContent,
  links,
}) => {
  const bodyText = String(message?.content || '').trim();
  const parsed = parseScheduleNoticeContent(bodyText);
  const detailHref = links?.contractDetailHref?.(message) || '';
  const introText = parsed.intro.join('\n');

  return (
    <>
      {introText && (
        <div className="system-message-card__text">
          {formatMessageContent(introText)}
        </div>
      )}
      <div className="system-message-card__schedule-summary">
        <div className="system-message-card__schedule-heading">
          <i className="fa-regular fa-calendar-check" aria-hidden="true" />
          <span>更新後のスケジュール</span>
        </div>
        {parsed.events.length === 0 ? (
          <div className="system-message-card__text system-message-card__text--muted">
            スケジュール情報を確認してください。
          </div>
        ) : (
          <div className="system-message-card__schedule-events">
            {parsed.events.map((event, eventIndex) => (
              <div key={`${event.title}-${eventIndex}`} className="system-message-card__schedule-event">
                <div className="system-message-card__schedule-event-title">{event.title}</div>
                {event.schedules.length === 0 ? (
                  <div className="system-message-card__schedule-empty">スケジュール未設定</div>
                ) : (
                  event.schedules.map((schedule, scheduleIndex) => (
                    <div key={`${event.title}-${scheduleIndex}`} className="system-message-card__schedule-row">
                      <div className="system-message-card__schedule-main">
                        <span className="system-message-card__schedule-date">{schedule.date || '日付未設定'}</span>
                        <span className="system-message-card__schedule-time">{schedule.time || '--:--'}</span>
                      </div>
                      <div className="system-message-card__schedule-note">{schedule.note || '内容未設定'}</div>
                      {schedule.place && (
                        <div className="system-message-card__schedule-place">
                          <i className="fa-solid fa-location-dot" aria-hidden="true" />
                          <span>{schedule.place}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {renderActionLink(detailHref, '詳細を見る')}
    </>
  );
};

const buildReminderNoticeDescriptor = ({
  message,
  parseReminderContent,
  contractDetailHref,
  eventDetailHref,
  showCardIcon = false,
  systemToneClass = '',
}) => {
  const parsed = parseReminderContent?.(message) || {};
  const header = getReminderHeader(message, parsed.kind);
  const eventUuid =
    parsed?.schedule?.event_uuid ||
    message?.reminder_event_uuid ||
    message?.contract_event_uuid ||
    message?.event_uuid ||
    message?.base_event_uuid ||
    null;
  const detailHref =
    contractDetailHref?.(message) ||
    eventDetailHref?.(eventUuid, message) ||
    '';

  return {
    ...buildSystemRichDescriptor({
      title: header,
      content: (
        <>
          {parsed.eventTitle && (
            <div className="system-message-card__text">
              「{[parsed.eventTitle, parsed.eventPerf].filter(Boolean).join(' ')}」の【
              {parsed.scheduleTitle}
              】が{header.includes('今日') ? 'まもなく開始します。' : '明日になりました。'}
            </div>
          )}
          {(parsed.dateTime || parsed.place) && (
            <div className="system-message-card__meta">
              {parsed.dateTime && (
                <div className="system-message-card__meta-row">
                  <i className="fa-regular fa-calendar" aria-hidden="true" />
                  <span>{parsed.dateTime}</span>
                </div>
              )}
              {parsed.place && (
                <div className="system-message-card__meta-row">
                  <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  <span>{parsed.place}</span>
                </div>
              )}
            </div>
          )}
          {renderActionLink(detailHref, '詳細を見る')}
        </>
      ),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    }),
  };
};

export const renderIndividualSystemMessage = ({
  message,
  isSystemMessage = false,
  showCardIcon = false,
  systemToneClass = '',
  formatMessageContent,
  parseReminderContent,
  cachedFlyerUrl,
  buildFlyerUrl,
  pickFlyerFilename,
  resolveGenreBadge,
  formatEventDate = (value) => String(value || ''),
  formatEventTime = (value) => String(value || '').slice(0, 5),
  titles = {},
  links = {},
  extractFirstUrl,
  hideUnsupportedSystemMessages = false,
}) => {
  const format = message?.message_format;

  if (isPartAnnouncementFormat(format)) {
    return renderSystemDescriptorCard(buildSystemTextDescriptor({
      title: titles.partNotice || 'お知らせが届きました',
      content: formatMessageContent(message?.content),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    }));
  }

  if (isStageSeatingShareAnnouncementFormat(format)) {
    return renderSystemDescriptorCard(buildSystemRichDescriptor({
      title: titles.stageSeatingShared || resolveStageSeatingShareTitle(message?.content),
      content: renderStageSeatingShareContent({
        message,
        formatMessageContent,
        extractFirstUrl,
      }),
      showIcon: showCardIcon,
      iconClass: 'fa-chair',
      cardClass: systemToneClass,
    }));
  }

  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CANCEL_COMP_DECIDED) {
    return renderSystemDescriptorCard(buildSystemTextDescriptor({
      title: titles.cancelCompDecided || '中止保証金額が確定しました',
      content: formatMessageContent(message?.content),
      extra: renderActionLink(links?.contractDetailHref?.(message) || '', '本契約詳細を見る'),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    }));
  }

  if (isInvoiceResubmissionRequestedFormat(format)) {
    return renderSystemDescriptorCard(buildSystemTextDescriptor({
      title: titles.invoiceResubmissionRequested || '請求書の再発行依頼が届きました',
      content: formatMessageContent(message?.content),
      extra: renderActionLink(links?.contractDetailHref?.(message) || '', '本契約詳細を見る'),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    }));
  }

  if (isOfferOrContractAnnouncement(format)) {
    if (isScheduleNotifyOnlyUpdate(message)) {
      return renderSystemDescriptorCard(buildSystemRichDescriptor({
        title: '重要: スケジュールが更新されました',
        content: renderScheduleUpdateAnnouncementContent({
          message,
          formatMessageContent,
          links,
        }),
        showIcon: showCardIcon,
        iconClass: 'fa-calendar-check',
        cardClass: systemToneClass,
      }));
    }
    const flyerUrl = resolveEventAnnouncementFlyerUrl({
      message,
      cachedFlyerUrl,
      buildFlyerUrl,
      pickFlyerFilename,
    });
    const flyerCardClass = [
      systemToneClass,
      flyerUrl ? 'system-message-card--with-flyer' : '',
    ].filter(Boolean).join(' ');
    return renderSystemDescriptorCard(buildSystemRichDescriptor({
      title: resolveAnnouncementHeader(format, titles, message),
      content: renderEventAnnouncementContent({
        message,
        formatMessageContent,
        flyerUrl,
        resolveGenreBadge,
        formatEventDate,
        formatEventTime,
        links,
      }),
      showIcon: showCardIcon,
      cardClass: flyerCardClass,
    }));
  }

  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_DECLINE) {
    const declineHref = links?.holdDeclineHref?.(message) || '';
    const declineBody =
      String(message?.content || '').trim() ||
      titles.holdDeclineBody ||
      DEFAULT_HOLD_DECLINE_BODY;
    return renderSystemTextCard({
      title: titles.holdDecline || DEFAULT_HOLD_DECLINE_TITLE,
      content: formatMessageContent(declineBody),
      extra: renderActionLink(declineHref, '対象オファーリンク'),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    });
  }

  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONCIERGE_RESULT) {
    const conciergeHref =
      extractFirstUrl?.(message?.content) || links?.conciergeFallbackHref || '';
    return renderSystemTextCard({
      title: titles.conciergeResult || 'コンシェルジュ申込の結果',
      content: formatMessageContent(message?.content),
      extra: renderActionLink(
        conciergeHref,
        'コンシェルジュページを開く',
        'system-message-card__link concierge-result-button',
      ),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    });
  }

  if (format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) {
    const reminder = buildReminderNoticeDescriptor({
      message,
      parseReminderContent,
      contractDetailHref: links?.contractDetailHref,
      eventDetailHref: links?.eventDetailHref,
      showCardIcon,
      systemToneClass,
    });
    return renderSystemDescriptorCard(reminder);
  }

  if (format === 'system_notice') {
    return renderSystemDescriptorCard(buildSystemTextDescriptor({
      title: titles.systemNotice || 'システム通知',
      content: formatMessageContent(message?.content || 'システム通知が届きました。'),
      showIcon: showCardIcon,
      cardClass: systemToneClass,
    }));
  }

  if (isSystemMessage) {
    if (hideUnsupportedSystemMessages) return null;
    const formatLabel = String(format || '').trim() || 'unknown';
    const fallbackContent = String(message?.content || '').trim() || 'このメッセージ形式には対応していません。';
    return renderSystemDescriptorCard(buildSystemTextDescriptor({
      title: titles.unsupported || '未対応のメッセージ',
      content: `message_format: ${formatLabel}\n${fallbackContent}`,
      showIcon: showCardIcon,
      cardClass: systemToneClass,
      forceMuted: true,
    }));
  }

  return null;
};
